import { ReactNode } from 'react'

// Components
import { IconPin, Layout, IconSoundcloud, IconBeatstars, IconYoutube, IconInstagram } from '@tracklib/kit'
import { Avatar } from 'kit'
import EditAvatar from 'components/profile/editavatar'
import UserBadges from 'components/profile/userbadges'

// Hooks
import { useProfile } from 'modules/profile/hooks'

// Types
import type { Profile } from 'types'

// Utils
import { COUNTRIES } from '@constants'

import styles from './userlayout.css'

const renderIcon = (network: string): ReactNode | null => {
  switch (network) {
    case 'soundcloud':
      return <IconSoundcloud className={styles.soundcloud} />
    case 'beatstars':
      return <IconBeatstars className={styles.beatstars} />
    case 'youtube':
      return <IconYoutube className={styles.youtube} />
    case 'instagram':
      return <IconInstagram className={styles.instagram} />
    default:
      return null
  }
}

const SocialLink = ({ href, network }: { href: string; network: string }): JSX.Element | null => {
  if (!href) {
    return null
  }
  return (
    <li className={styles.socialLink}>
      <a href={href} target="_blank" rel="noopener noreferrer nofollow">
        {renderIcon(network)}
      </a>
    </li>
  )
}

type UserLayoutProps = {
  user: Profile
  children: ReactNode
}

const UserLayout = ({ user, children }: UserLayoutProps): JSX.Element => {
  const profile = useProfile()
  const canEdit = user?.id === profile?.id
  const AvatarEl = canEdit ? EditAvatar : Avatar
  const websiteWithoutHttp = user.website?.replace(/(^\w+:|^)\/\//, '')
  const hasSocialLinks = user.soundcloud || user.beatstars || user.instagram || user.youtube
  return (
    <Layout.FullWidth maxWidth={1100}>
      <header className={styles.header}>
        <div className={styles.avatarWrapper}>
          <span className={styles.circle} />
          <AvatarEl image={user.avatar} className={styles.avatar} sizes="(min-width: 769px) 180px, 78px" disabled />
        </div>
        {user.alias && (
          <div className={styles.content}>
            <h1 className={styles.name}>
              {user.alias} <UserBadges groups={user.groups} />
            </h1>
            <div className={styles.country} datatesting="country">
              <IconPin />
              {COUNTRIES[user.country as keyof typeof COUNTRIES].country_name}
            </div>
            {user.description && <p className={styles.description}>{user.description}</p>}
            {(hasSocialLinks || websiteWithoutHttp) && (
              <div className={styles.links}>
                {hasSocialLinks && (
                  <ol className={styles.socialLinks}>
                    <SocialLink href={user.beatstars} network="beatstars" />
                    <SocialLink href={user.instagram} network="instagram" />
                    <SocialLink href={user.youtube} network="youtube" />
                    <SocialLink href={user.soundcloud} network="soundcloud" />
                  </ol>
                )}
                {websiteWithoutHttp && (
                  <a href={`http://${websiteWithoutHttp}`} target="_blank" rel="noopener noreferrer nofollow" className={styles.website}>
                    {websiteWithoutHttp}
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </header>
      {children}
    </Layout.FullWidth>
  )
}

export default UserLayout
